import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth } from '../../firebase';
import Diff from './Diff';
import PullNav from './PullNav';
import {
    supportsFolderAccess,
    pickWriterFolder,
    scanWriterFolder,
    readHandleText,
    remoteToMarkdown,
    canonicalMarkdown,
    markdownToHtml,
    contentHash,
    fetchWorkspaceByDomain,
    listWorkspaceDocuments,
    fetchWorkspaceDocDetail,
    updateWorkspaceDocContent,
    createWorkspacePage,
    stampManifest,
} from './writerSyncUtils';
import '../../css/WriterPull.css';

const STATUS_ORDER = ['conflict', 'changed', 'new', 'error'];
const STATUS_TITLES = {
    conflict: 'Also changed in this workspace since Writer last synced — review carefully',
    changed: 'Updated in Bluebird Writer',
    new: 'New notes from Writer — will be created as Pages',
    error: 'Could not compare',
};
const OUTCOME_LABELS = {
    applied: 'Applied',
    created: 'Created',
    skipped: 'Skipped',
    error: 'Failed',
};

// Pull this workspace's documents from a local Bluebird Writer folder.
// Writer stores them under Bluebird/Workspaces/<label>/ and records them in
// its manifest with ws:<workspaceId>: keys — that id scoping is what keeps
// this page from ever touching personal docs or another workspace's.
function WorkspaceWriterPull() {
    const { customDomain } = useParams();
    const navigate = useNavigate();
    const [uid, setUid] = useState('');
    const [authResolved, setAuthResolved] = useState(false);
    const [workspace, setWorkspace] = useState(null);
    const [workspaceError, setWorkspaceError] = useState('');

    const [stage, setStage] = useState('intro');
    const [progress, setProgress] = useState('');
    const [plan, setPlan] = useState(null);
    const [upToDate, setUpToDate] = useState(0);
    const [missing, setMissing] = useState(0);
    const [notLinked, setNotLinked] = useState(false); // manifest has no entries for this workspace
    const [approved, setApproved] = useState(() => new Set());
    const [expanded, setExpanded] = useState(() => new Set());
    const [outcomes, setOutcomes] = useState(null);
    const [applying, setApplying] = useState(false);
    const [manifestStamped, setManifestStamped] = useState(null);
    const [error, setError] = useState('');
    const [scanState, setScanState] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setUid(user ? user.uid : '');
            setAuthResolved(true);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!uid || !customDomain) return;
        let cancelled = false;
        (async () => {
            try {
                const ws = await fetchWorkspaceByDomain(customDomain);
                if (!cancelled) setWorkspace(ws);
            } catch (err) {
                if (!cancelled) setWorkspaceError(err.message);
            }
        })();
        return () => { cancelled = true; };
    }, [uid, customDomain]);

    const choose = async () => {
        setError('');
        let dirHandle;
        try {
            dirHandle = await pickWriterFolder();
        } catch {
            return; // user cancelled the picker
        }
        setStage('planning');
        try {
            setProgress('Reading your Writer folder…');
            const { files, manifest, manifestHandle } = await scanWriterFolder(dirHandle);
            setScanState({ manifest, manifestHandle });

            const wsPrefix = `ws:${workspace.id}:`;
            const entries = Object.entries(manifest?.docs || {}).filter(([k]) => k.startsWith(wsPrefix));
            const allMappedRels = new Set(Object.values(manifest?.docs || {}).map((e) => e.rel));

            if (!entries.length) {
                setNotLinked(true);
                setPlan([]);
                setUpToDate(0);
                setMissing(0);
                setStage('review');
                return;
            }
            setNotLinked(false);

            setProgress('Listing workspace documents…');
            const { spaces, pages } = await listWorkspaceDocuments(workspace.id);
            const byName = new Map([
                ...spaces.map((s) => [`Space:${s.name}`, { ...s, type: 'Space' }]),
                ...pages.map((p) => [`Page:${p.page_name}`, { ...p, type: 'Page' }]),
            ]);

            const rows = [];
            let upToDateCount = 0;
            let missingCount = 0;

            // 1. Manifest-mapped documents of THIS workspace
            for (const [key, entry] of entries) {
                const typeAndName = key.slice(wsPrefix.length);
                const sep = typeAndName.indexOf(':');
                const type = typeAndName.slice(0, sep);
                const name = typeAndName.slice(sep + 1);
                setProgress(`Comparing ${name}…`);

                const file = files.find((f) => f.rel === entry.rel);
                if (!file) {
                    missingCount++;
                    continue;
                }
                try {
                    const listed = byName.get(`${type}:${name}`);
                    if (!listed) {
                        rows.push({ rowKey: key, type, name, rel: entry.rel, status: 'error', message: 'Not found in this workspace (deleted or renamed here?)' });
                        continue;
                    }
                    const localMd = await readHandleText(file.handle);
                    const detail = await fetchWorkspaceDocDetail(workspace.id, type, listed.id);
                    const remoteMd = remoteToMarkdown(detail.content || '');
                    const canonLocal = canonicalMarkdown(localMd);
                    if (canonLocal === remoteMd) {
                        upToDateCount++;
                        continue;
                    }
                    rows.push({
                        rowKey: key,
                        type,
                        name,
                        rel: entry.rel,
                        docId: listed.id,
                        status: String(detail.updated_at) === String(entry.remoteUpdatedAt) ? 'changed' : 'conflict',
                        localMd,
                        version: detail.version,
                        remoteMd,
                        canonLocal,
                    });
                } catch (err) {
                    rows.push({ rowKey: key, type, name, rel: entry.rel, status: 'error', message: err.message });
                }
            }

            // 2. New local notes inside this workspace's folder
            const labelDir = entries[0][1].rel.match(/^(Bluebird\/Workspaces\/[^/]+\/)/)?.[1];
            if (labelDir) {
                const spaceIdByName = new Map(spaces.map((s) => [s.name, s.id]));
                for (const file of files) {
                    if (!file.rel.startsWith(labelDir) || allMappedRels.has(file.rel)) continue;
                    const title = file.rel.split('/').pop().replace(/\.(md|markdown)$/i, '');
                    setProgress(`Checking "${title}"…`);
                    if (byName.has(`Page:${title}`) || byName.has(`Space:${title}`)) {
                        rows.push({ rowKey: `new:${file.rel}`, type: 'Page', name: title, rel: file.rel, status: 'error', message: 'A document with this name already exists in the workspace but is not linked — pull it in Writer first' });
                        continue;
                    }
                    // A note filed under <label>/<SpaceName>/ becomes a page in that Space.
                    const subDir = file.rel.slice(labelDir.length).split('/');
                    const parentSpaceId = subDir.length > 1 ? spaceIdByName.get(subDir[0]) : undefined;
                    try {
                        rows.push({
                            rowKey: `new:${file.rel}`,
                            type: 'Page',
                            name: title,
                            rel: file.rel,
                            status: 'new',
                            localMd: await readHandleText(file.handle),
                            parentSpaceId,
                        });
                    } catch (err) {
                        rows.push({ rowKey: `new:${file.rel}`, type: 'Page', name: title, rel: file.rel, status: 'error', message: err.message });
                    }
                }
            }

            setPlan(rows);
            setUpToDate(upToDateCount);
            setMissing(missingCount);
            setApproved(new Set());
            setExpanded(new Set());
            setOutcomes(null);
            setManifestStamped(null);
            setStage('review');
        } catch (err) {
            setError(err.message || String(err));
            setStage('intro');
        } finally {
            setProgress('');
        }
    };

    const toggleApproval = (rowKey) => {
        if (outcomes) return;
        setApproved((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    };

    const toggleExpanded = (rowKey) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    };

    const apply = async () => {
        if (!plan || !approved.size) return;
        setApplying(true);
        const results = new Map();
        const stamps = [];
        let done = 0;
        for (const row of plan) {
            if (!approved.has(row.rowKey) || row.status === 'error') {
                results.set(row.rowKey, { outcome: 'skipped' });
                continue;
            }
            setProgress(`Applying ${++done}/${approved.size}: ${row.name}…`);
            try {
                const html = markdownToHtml(row.localMd);
                let docId = row.docId;
                if (row.status === 'new') {
                    await createWorkspacePage(workspace.id, row.name, html, row.parentSpaceId);
                    const { pages } = await listWorkspaceDocuments(workspace.id);
                    docId = pages.find((p) => p.page_name === row.name)?.id;
                } else {
                    await updateWorkspaceDocContent(workspace.id, row.type, row.docId, row.version, html);
                }
                if (docId) {
                    const fresh = await fetchWorkspaceDocDetail(workspace.id, row.type, docId);
                    stamps.push({
                        key: `ws:${workspace.id}:${row.type}:${row.name}`,
                        rel: row.rel,
                        hash: contentHash(row.localMd),
                        remoteUpdatedAt: fresh.updated_at,
                    });
                }
                results.set(row.rowKey, { outcome: row.status === 'new' ? 'created' : 'applied' });
            } catch (err) {
                results.set(row.rowKey, { outcome: 'error', message: err.message });
            }
        }
        if (stamps.length && scanState?.manifest) {
            const ok = await stampManifest(scanState.manifestHandle, scanState.manifest, stamps);
            setManifestStamped(ok);
        }
        setOutcomes(results);
        setApplying(false);
        setProgress('');
    };

    const pending = plan || [];
    const summary = () => {
        if (!outcomes) return null;
        const counts = {};
        for (const r of outcomes.values()) counts[r.outcome] = (counts[r.outcome] || 0) + 1;
        const parts = [];
        if (counts.applied) parts.push(`${counts.applied} updated`);
        if (counts.created) parts.push(`${counts.created} created`);
        if (counts.skipped) parts.push(`${counts.skipped} left untouched`);
        if (counts.error) parts.push(`${counts.error} failed`);
        return parts.join(' · ');
    };

    if (!authResolved) {
        return (
            <div className="WriterPull__page">
                <p className="WriterPull__muted">Checking authentication…</p>
            </div>
        );
    }

    const navItems = [
        { label: 'Back to Settings', icon: 'fas fa-arrow-left', to: `/workspace/${customDomain}/settings` },
        { label: 'Workspace', icon: 'fas fa-users', to: `/workspace/${customDomain}` },
        { label: 'Home', icon: 'fas fa-home', to: '/' },
    ];

    if (!uid) {
        return (
            <div className="WriterPull__page">
                <div className="WriterPull__card">
                    <PullNav items={[{ label: 'Home', icon: 'fas fa-home', to: '/' }]} />
                    <h2>Pull Workspace Documents from Bluebird Writer</h2>
                    <p>You need to be signed in to pull changes from your Writer folder.</p>
                    <button className="WriterPull__primary" onClick={() => navigate('/login')}>Login</button>
                </div>
            </div>
        );
    }

    return (
        <div className="WriterPull__page">
            <div className="WriterPull__card">
                <PullNav items={navItems} disabled={stage === 'planning' || applying} />
                <h2>Pull from Bluebird Writer{workspace ? ` — ${workspace.name}` : ''}</h2>

                {workspaceError && <p className="WriterPull__error">{workspaceError}</p>}

                {stage === 'intro' && (
                    <>
                        <p>
                            Choose the folder Bluebird Writer saves your notes in. We'll compare this
                            workspace's notes (from <code>Bluebird/Workspaces/…</code>) against its documents
                            and show you every difference. <strong>Nothing is changed unless you approve it,
                            document by document.</strong>
                        </p>
                        <ul className="WriterPull__notes">
                            <li>Only documents belonging to this workspace are considered.</li>
                            <li>New notes in this workspace's folder can be created as Pages (a note inside a Space's folder joins that Space).</li>
                            <li>Your workspace role applies — updates need editor rights.</li>
                            <li>Every update keeps a history snapshot, so changes can be recovered.</li>
                        </ul>
                        {!supportsFolderAccess() && (
                            <p className="WriterPull__error">
                                This browser can't open local folders (File System Access API). Chrome and
                                Edge support it.
                            </p>
                        )}
                        {error && <p className="WriterPull__error">{error}</p>}
                        <button
                            className="WriterPull__primary"
                            disabled={!supportsFolderAccess() || !workspace}
                            onClick={choose}
                        >
                            Choose Writer folder…
                        </button>
                    </>
                )}

                {stage === 'planning' && <p className="WriterPull__muted">{progress || 'Comparing…'}</p>}

                {stage === 'review' && (
                    <>
                        {notLinked ? (
                            <p className="WriterPull__error">
                                This Writer folder has no notes linked to this workspace yet. Connect the
                                workspace in Bluebird Writer (Sync → Add key) and pull it there first —
                                that link is what lets this page match notes to documents safely.
                            </p>
                        ) : (
                            <p className="WriterPull__summary-line">
                                {pending.length
                                    ? `${pending.length} ${pending.length === 1 ? 'difference' : 'differences'}`
                                    : 'Everything is in sync'}
                                {` · ${upToDate} up to date`}
                                {missing > 0 && ` · ${missing} not present locally`}
                            </p>
                        )}

                        {STATUS_ORDER.map((status) => {
                            const group = pending.filter((p) => p.status === status);
                            if (!group.length) return null;
                            return (
                                <div key={status} className="WriterPull__group">
                                    <h3 className={`WriterPull__group-title WriterPull__group-title--${status}`}>
                                        {STATUS_TITLES[status]} ({group.length})
                                    </h3>
                                    {group.map((row) => {
                                        const outcome = outcomes?.get(row.rowKey);
                                        return (
                                            <div key={row.rowKey} className="WriterPull__row">
                                                <div className="WriterPull__row-main">
                                                    <label className="WriterPull__row-label" title={row.message || row.rel}>
                                                        <input
                                                            type="checkbox"
                                                            disabled={row.status === 'error' || !!outcomes}
                                                            checked={approved.has(row.rowKey)}
                                                            onChange={() => toggleApproval(row.rowKey)}
                                                        />
                                                        <span className="WriterPull__type">{row.type}</span>
                                                        <span className="WriterPull__name">{row.name}</span>
                                                    </label>
                                                    {row.status !== 'error' && (
                                                        <button
                                                            className="WriterPull__ghost"
                                                            onClick={() => toggleExpanded(row.rowKey)}
                                                        >
                                                            {expanded.has(row.rowKey) ? 'Hide' : row.status === 'new' ? 'Preview' : 'Diff'}
                                                        </button>
                                                    )}
                                                    {outcome && (
                                                        <span
                                                            className={`WriterPull__outcome WriterPull__outcome--${outcome.outcome}`}
                                                            title={outcome.message || ''}
                                                        >
                                                            {OUTCOME_LABELS[outcome.outcome]}
                                                        </span>
                                                    )}
                                                </div>
                                                {row.message && !outcomes && (
                                                    <p className="WriterPull__row-note">{row.message}</p>
                                                )}
                                                {expanded.has(row.rowKey) && (
                                                    <Diff oldText={row.remoteMd || ''} newText={row.status === 'new' ? row.localMd : row.canonLocal} />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}

                        <div className="WriterPull__footer">
                            <span className="WriterPull__muted">
                                {applying
                                    ? progress
                                    : outcomes
                                        ? `${summary()}${manifestStamped === false ? ' · could not update the Writer manifest (folder was read-only)' : ''}`
                                        : pending.length
                                            ? `${approved.size} of ${pending.length} approved — unapproved documents stay exactly as they are`
                                            : ''}
                            </span>
                            <span className="WriterPull__footer-actions">
                                <button className="WriterPull__ghost" disabled={applying} onClick={() => { setStage('intro'); setPlan(null); setOutcomes(null); setUpToDate(0); setNotLinked(false); }}>
                                    {outcomes ? 'Start over' : 'Back'}
                                </button>
                                {!outcomes && pending.length > 0 && (
                                    <button
                                        className="WriterPull__primary"
                                        disabled={applying || approved.size === 0}
                                        onClick={apply}
                                    >
                                        Apply {approved.size || ''} approved
                                    </button>
                                )}
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default WorkspaceWriterPull;
